import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  CONSISTENCY_CELL_OPTIONS,
  CONSISTENCY_VARIATION_KINDS,
  DEFAULT_CONSISTENCY_SHEET_STATE,
  buildConsistencySheetRequest,
  hasSourceImage,
  type ConsistencyCellCount,
  type ConsistencySheetFormState,
  type ConsistencyVariationKind,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

export const ConsistencySheetPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<ConsistencySheetFormState>(DEFAULT_CONSISTENCY_SHEET_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.consistencySheet);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image of the subject is required.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildConsistencySheetRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <GridViewIcon fontSize="small" />
        <Typography variant="subtitle2">Consistency sheet</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image of the subject.</Alert>
      )}

      <Stack direction="row" spacing={1}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="cs-cells-label">Cells</InputLabel>
          <Select
            labelId="cs-cells-label"
            label="Cells"
            value={state.sheetCellCount}
            onChange={e =>
              setState(s => ({ ...s, sheetCellCount: Number(e.target.value) as ConsistencyCellCount }))
            }
          >
            {CONSISTENCY_CELL_OPTIONS.map(n => (
              <MenuItem key={n} value={n}>{`${n} cells`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="cs-kind-label">Vary by</InputLabel>
          <Select
            labelId="cs-kind-label"
            label="Vary by"
            value={state.variationKind}
            onChange={e =>
              setState(s => ({ ...s, variationKind: e.target.value as ConsistencyVariationKind }))
            }
          >
            {CONSISTENCY_VARIATION_KINDS.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <TextField
        label="Additional prompt (optional)"
        value={state.additionalPrompt}
        onChange={e => setState(s => ({ ...s, additionalPrompt: e.target.value }))}
        placeholder="Steer the variations"
        size="small"
        fullWidth
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Consistency sheet failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <GridViewIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Generating…' : 'Build sheet'}
        </Button>
      </Box>
    </Stack>
  );
};
